import type { ComponentType } from '../schemas/validation';

/**
 * Keep in sync with mapSeoIssueToAction in
 * webbuilder-api/src/services/roary/seoIssueFix.ts
 *
 * Most Roary fixes write one mapped field (or alt text). Thin content is a
 * frontend-only component picker — it is not mapped on the API.
 *
 * Open Graph image also needs a real URL: the site Social Image or a page photo.
 */

export const CANNOT_AUTO_FIX_SUMMARY =
  "Roary can't auto-fix this one — it's a content or structure change you'll want to make yourself.";

export const THIN_CONTENT_COMPONENT_OPTIONS: { type: ComponentType; label: string }[] = [
  { type: 'hero', label: 'Hero' },
  { type: 'testimonials', label: 'Testimonials' },
  { type: 'faq', label: 'FAQ' },
  { type: 'text-blocks', label: 'Text Blocks' },
  { type: 'features', label: 'Features' },
];

export type RoaryFixMaterial = {
  siteOgImage?: string;
  pageImageUrls?: string[];
  imageIssueCount?: number;
};

export function isThinContentIssue(message: string): boolean {
  return message.toLowerCase().includes('thin content');
}

export function isRoaryFixableIssue(
  message: string,
  isBlogPost: boolean,
  material?: RoaryFixMaterial
): boolean {
  const msg = message.toLowerCase();

  if (isThinContentIssue(message)) return !isBlogPost;

  if (msg.includes('alt text') || msg.includes('missing alt')) {
    if (isBlogPost) return false;
    if (material && material.imageIssueCount !== undefined) {
      return material.imageIssueCount > 0;
    }
    return true;
  }
  if (msg.includes('open graph image')) {
    if (isBlogPost) return true;
    if (!material) return true;
    const hasSiteOg = Boolean(material.siteOgImage?.trim());
    const hasPagePhoto = (material.pageImageUrls ?? []).some(url => Boolean(url?.trim()));
    return hasSiteOg || hasPagePhoto;
  }
  if (msg.includes('open graph description')) return true;
  if (msg.includes('canonical')) return !isBlogPost;
  if (msg.includes('meta title')) return true;
  if (msg.includes('meta description')) return true;

  return false;
}
